import { useEffect, useState } from 'react';
import { api } from '../../lib/api';

interface Area {
  id: string;
  name: string;
  radar_embed: string;
  nws_forecast?: string;
}

interface MapsMeta {
  areas: Area[];
  regional_radar: {
    station: string;
    name: string;
    nws_url: string;
    rainviewer: string;
  };
}

/**
 * Live weather radar for NE Indiana (RainViewer + NWS KIWX link).
 * No API key required.
 */
export function RadarWidget() {
  const [meta, setMeta] = useState<MapsMeta | null>(null);
  const [areaId, setAreaId] = useState('fort-wayne');
  const [error, setError] = useState('');
  const [tick, setTick] = useState(0);

  useEffect(() => {
    api<MapsMeta>('/api/local-maps')
      .then(setMeta)
      .catch((e) => setError(e instanceof Error ? e.message : 'Radar unavailable'));
  }, []);

  // Soft-refresh iframe every 5 minutes so loops stay current
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 5 * 60_000);
    return () => clearInterval(t);
  }, []);

  if (error) {
    return (
      <div>
        <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-slate-400">
          Live radar
        </h3>
        <p className="text-sm text-slate-500">{error}</p>
      </div>
    );
  }

  if (!meta) {
    return (
      <div>
        <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-slate-400">
          Live radar
        </h3>
        <p className="text-sm text-slate-500">Loading radar…</p>
      </div>
    );
  }

  const useRegional = areaId === 'regional';
  const area = meta.areas.find((a) => a.id === areaId) || meta.areas[0];
  const src = useRegional
    ? `${meta.regional_radar.rainviewer}&_t=${tick}`
    : `${area.radar_embed}&_t=${tick}`;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-bold uppercase tracking-wide text-slate-400">
          Live radar
        </h3>
        <div className="ml-auto flex flex-wrap gap-1">
          {meta.areas.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => setAreaId(a.id)}
              className={`rounded-lg px-2 py-0.5 text-[11px] font-semibold ${
                areaId === a.id
                  ? 'bg-violet-600 text-white'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              {a.name.replace(', IN', '')}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setAreaId('regional')}
            className={`rounded-lg px-2 py-0.5 text-[11px] font-semibold ${
              areaId === 'regional'
                ? 'bg-violet-600 text-white'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            Region (KIWX)
          </button>
        </div>
      </div>
      <div className="relative min-h-0 flex-1 overflow-hidden rounded-xl border border-slate-700 bg-black/40">
        <iframe
          key={`${areaId}-${tick}`}
          title="Live weather radar"
          src={src}
          className="h-full w-full border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allow="fullscreen"
        />
      </div>
      <div className="mt-1 flex flex-wrap gap-2 text-[10px] text-slate-500">
        <span>RainViewer · free radar loops</span>
        <a
          href={meta.regional_radar.nws_url}
          target="_blank"
          rel="noreferrer"
          className="text-violet-400 hover:underline"
        >
          Open NWS {meta.regional_radar.station} ↗
        </a>
        {!useRegional && area?.nws_forecast && (
          <a
            href={area.nws_forecast}
            target="_blank"
            rel="noreferrer"
            className="text-violet-400 hover:underline"
          >
            NWS forecast ↗
          </a>
        )}
      </div>
    </div>
  );
}
